import { CommonButton, Loading, Modal, Select } from '@chocolate-soup-inc/cs-frontend-components';
import { useCallback, useMemo, useState } from 'react';

import clsx from 'clsx';
import { toast } from 'react-toastify';
import { TShipmentType } from '../../../entities/shipments/queries';
import { CANADAPOST_SERVICE_OPTIONS, CARRIER_OPTIONS, DHL_SERVICE_OPTIONS, PUROLATOR_SERVICE_OPTIONS, UPS_SERVICE_OPTIONS } from '../../../entities/shipments/shared';
import { TCarriers, useUpdateShipmentMutation } from '../../../generated/graphql';
import styles from './Form.module.scss';

export type TSetAsReadyToShipGropuedModalProps = {
  shipment: TShipmentType;
  onCancel: () => void;
  onSuccess: () => void;
};

export const OverrideCarrierModal = (props: TSetAsReadyToShipGropuedModalProps) => {
  const { shipment, onCancel, onSuccess } = props;

  const [overrideCarrier, setOverrideCarrier] = useState<string | null>(null);
  const [overrideService, setOverrideService] = useState<string | null>();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const carrierOpts = useMemo(() => Object.entries(CARRIER_OPTIONS).map(([key, value]) => ({ label: value as string, value: key as string })), []);

  const serviceOpts = useMemo(() => {
    switch (overrideCarrier) {
      case TCarriers.CanadaPost:
        return Object.entries(CANADAPOST_SERVICE_OPTIONS).map(([key, value]) => ({
          label: value as string,
          value: key as string
        }));
      case TCarriers.Ups:
        return Object.entries(UPS_SERVICE_OPTIONS).map(([key, value]) => ({
          label: value as string,
          value: key as string
        }));
      case TCarriers.PurolatorCa:
        return Object.entries(PUROLATOR_SERVICE_OPTIONS).map(([key, value]) => ({
          label: value as string,
          value: key as string
        }));
      case TCarriers.DhlExpressCanada:
        return Object.entries(DHL_SERVICE_OPTIONS).map(([key, value]) => ({
          label: value as string,
          value: key as string
        }));
      default:
        return [];
    }
  }, [overrideCarrier]);

  const [updateShipment] = useUpdateShipmentMutation();

  const onSubmit = useCallback(async () => {
    if (overrideCarrier && !overrideService) return toast.error('Please select a service if an override carrier is set.');

    try {
      setIsLoading(true);

      await updateShipment({
        variables: {
          id: shipment.id as string,
          companyId: shipment.companyId as string,
          version: shipment._version as number,
          input: {
            overrideCarrier,
            overrideService
          }
        }
      });

      setIsLoading(false);
      onSuccess();
    } catch (e) {
      console.warn('onSubmit', e);
      toast.error('There was an error: ' + e);
    }
  }, [shipment, updateShipment, overrideCarrier, overrideService, onSuccess]);

  const bottomComponent = useCallback(() => {
    if (!shipment) return <></>;
    return (
      <div className={styles.topComponent}>
        <CommonButton className={clsx(styles.addBtn)} leadingIcon='close' label='Cancel' disabled={isLoading} onClick={onCancel} variant='outlined' />
        <CommonButton className={clsx(styles.addBtn)} leadingIcon='check' label='Update' disabled={isLoading} onClick={onSubmit} variant='filled' />
        {isLoading && <Loading className={clsx(styles.loading)} />}
      </div>
    );
  }, [shipment, onCancel, onSubmit, isLoading]);

  return (
    <Modal
      headline='Override Shipment Carrier'
      size='small'
      supportingText='Please set the new Carrier and Service. You can restore the default values clearing your selections and updating.'
      closeModal={onCancel}
      showCustomButton={true}
      customButton={bottomComponent}>
      <div className={clsx(styles.containerCards)}>
        <Select
          label='Carrier'
          variant='outlined'
          value={overrideCarrier}
          name='overrideCarrier'
          options={carrierOpts}
          multiple={false}
          includeEmptyOption={true}
          onChange={(v) => {
            setOverrideCarrier(v as TCarriers);
            setOverrideService(null);
          }}
        />
        <Select
          label='Service'
          variant='outlined'
          value={overrideService}
          name='overrideService'
          options={serviceOpts}
          multiple={false}
          includeEmptyOption={true}
          onChange={(v) => {
            setOverrideService(v as string);
          }}
        />
      </div>
    </Modal>
  );
};
